import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { useLenis } from "~/hooks/use-lenis";
import { useI18n } from "~/i18n";
import { Header } from "~/components/header";
import { Footer } from "~/components/footer";
import { HeroSlider } from "~/components/sections/hero-slider";
import { PvSection } from "~/components/sections/pv-section";
import { Synopsis } from "~/components/sections/synopsis";
import { Characters } from "~/components/sections/characters";
import { NewsList } from "~/components/sections/news-list";
import { Merchandise } from "~/components/sections/merchandise";
import { NewsModal } from "~/components/news-modal";
import { CtaLinks } from "~/components/sections/cta-links";
import { useIpContentList } from "~/hooks/use-ip-content";
import { useNewsDetail } from "~/hooks/use-news-detail";
import { getIpPageData } from "~/data/ip-page-data";
import { resolveIpAssets } from "~/theme/resolve-theme";
import { filterVisibleNews, isNewsItemVisible } from "@laftel-ip-originals/notion/news-visibility";
import type { NewsDetail, NewsListItem } from "@laftel-ip-originals/notion/contracts";

export function IpMain() {
  const { slug, id } = useParams<{ slug: string; id: string }>();
  const { locale } = useI18n();
  const [focusCharId, setFocusCharId] = useState<number | null>(null);
  const [modalItemId, setModalItemId] = useState<string | null>(id ?? null);
  const lenisRef = useLenis(true);
  const { isLoading, hasGoods, goodsCoverImage, newsItems, ctaCategories, ctaIsLoading } =
    useIpContentList(slug);
  const { data: rawModalItem, isLoading: isModalLoading } = useNewsDetail(slug, modalItemId);
  const pageData = getIpPageData(slug);
  const assets = slug ? resolveIpAssets(slug) : null;
  const pvLocale = locale === "zh-cn" || locale === "zh-tw" ? "en" : locale;
  const pvVideoId = assets?.pvVideoIds?.[pvLocale] ?? assets?.pvVideoIds?.en;
  const pvPreviewSrc =
    assets?.pvPreviewByLocale?.[pvLocale] ?? assets?.pvPreviewByLocale?.en ?? assets?.pvPreview;

  /**
   * locale 번역이 없거나 미래 게시인 뉴스는 목록에서 숨긴다. 시각은 `Date.now()` 로
   * 렌더 시점에 평가 — 미래 게시 뉴스는 게시 시각이 지난 후 다음 페이지 로드에서 자동 노출.
   */
  const visibleNewsItems = useMemo(
    () => filterVisibleNews(newsItems, locale, Date.now()),
    [newsItems, locale],
  );
  /** 직링크로 미래/번역없는 뉴스 detail 을 열려 해도 목록과 동일 기준으로 suppress. */
  const modalItem = useMemo(() => {
    if (!rawModalItem) return null;
    return isNewsItemVisible(rawModalItem, locale, Date.now()) ? rawModalItem : null;
  }, [rawModalItem, locale]);

  useEffect(() => {
    setModalItemId(id ?? null);
  }, [id]);

  useEffect(() => {
    const onPopState = () => {
      const match = window.location.pathname.match(/\/news\/([^/]+)/);
      setModalItemId(match?.[1] ? decodeURIComponent(match[1]) : null);
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const openNews = useCallback(
    (item: NewsListItem) => {
      if (!slug) {
        return;
      }

      setModalItemId(item.id);
      window.history.pushState(null, "", `/${slug}/news/${encodeURIComponent(item.id)}`);
    },
    [slug],
  );

  const closeModal = useCallback(() => {
    if (!slug) {
      return;
    }

    setModalItemId(null);
    window.history.pushState(null, "", `/${slug}`);
  }, [slug]);

  const handleBadgeClick = useCallback((characterId: number) => {
    setFocusCharId(characterId);
    document.getElementById("character")?.scrollIntoView({ behavior: "smooth" });
    requestAnimationFrame(() => setFocusCharId(null));
  }, []);

  return (
    <>
      {/* 캐릭터 이미지 preload — hidden DOM에 미리 로드 */}
      {pageData?.characters && (
        <div aria-hidden className="hidden">
          {pageData.characters
            .flatMap((c) => [...(c.fullImg ? [c.fullImg] : []), ...c.faceImgs])
            .map((src) => (
              <img key={src} src={src} alt="" />
            ))}
        </div>
      )}
      <Header />
      <HeroSlider lenisRef={lenisRef} />
      <PvSection videoId={pvVideoId} previewSrc={pvPreviewSrc} />
      {pageData?.synopsis && (
        <Synopsis
          heading={pageData.synopsis.heading}
          text={pageData.synopsis.text}
          onBadgeClick={handleBadgeClick}
        />
      )}
      {pageData?.characters && (
        <Characters characters={pageData.characters} focusCharacterId={focusCharId} />
      )}
      <NewsList items={visibleNewsItems} isLoading={isLoading} onNewsClick={openNews} />
      <Merchandise hasGoods={hasGoods} coverImage={goodsCoverImage} isLoading={isLoading} />
      {pageData?.ctaLinks && (
        <CtaLinks
          links={pageData.ctaLinks.map((config) => ({
            ...config,
            platforms: ctaCategories[config.category] ?? [],
            loading: ctaIsLoading,
          }))}
        />
      )}
      <Footer />
      {(modalItem || (modalItemId && isModalLoading)) && (
        <NewsModal
          item={modalItem ?? EMPTY_NEWS_ITEM}
          locale={locale}
          loading={!modalItem}
          onClose={closeModal}
        />
      )}
    </>
  );
}

const EMPTY_NEWS_ITEM: NewsDetail = {
  bodyHtml: {},
  category: {
    en: "",
    ja: "",
    ko: "",
    "zh-cn": "",
    "zh-tw": "",
  },
  date: "",
  id: "",
  publishedAtMs: 0,
  title: {},
};
